import _ from "lodash";
import { PRE_LOAD_CONFIGURATIONS } from "Constants/permissionConstants";
import { getJsonItem } from "services/localStorage";
import {
  getUserInfo,
  isAdministratorRoute,
  isPublicPlatformRoute,
  fetchOrganizationAndUserIdForPublicPost,
} from "Login/modules/LoginModule/LoginUtils";
import {
  CONFIGURATION_ENTITY_LIST,
  MODULE_DEPENDENT_CONTROL_PARAMETERS_CONFIG,
} from "./ChildrenPageConstants";
import {
  readConfigurationFragmentV2,
  getConfigurationsFromCacheV2,
} from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import { parseConfigurationValue } from "Utils";
import { STAFF, ROLE_VISITOR } from "Constants/stringConstants";
import { source } from "Teacher/routes/NotificationSettings/modules/NotificationSettingsModule";
import { getAttendancePermissionIds } from "AttendanceDashboard/modules/Utils";
import { ATTENDANCE_CONSTANTS } from "Constants/attendanceConstants";
import { getPermissionsFromCache } from "ConfigurationsV2/modules/ConfigurationGraphqlHelpers";
import { getUserPrimaryGradeId } from "modules/CommonSelectors";
import { getIsModuleSettingsV2Enabled } from "Utils/ACLStoreUtils";

const stringifyConfigurationValue = ({ valueType, value }) => {
  switch (valueType) {
    case "BOOLEAN":
      return value ? "true" : "false";
    case "JSON":
    case "ARRAY":
      return JSON.stringify(value);
    default:
      return value;
  }
};

const getEntityIdBasedOnEntityType = ({
  entityType,
  userId,
  currentCurriculumProgramId,
  academicYearId,
  courseId,
  organizationId,
  hasChildContext,
  childId,
  identityId,
  gradeId,
  portalType = "PLANNER",
  learningCourseId,
}) => {
  const userInfoLocal = getUserInfo({ portalType });
  switch (entityType) {
    case "ORGANIZATION":
      return organizationId || userInfoLocal.org_id;
    case "USER": {
      const isParentLogin = _.get(userInfoLocal, "user_type") === "parent";
      const shouldUseChildId = isParentLogin && hasChildContext;
      const userIdFromParam = shouldUseChildId ? childId : userId;
      const defaultUserId = shouldUseChildId
        ? userInfoLocal.childID
        : userInfoLocal.id;

      return userIdFromParam || defaultUserId;
    }
    case "CURRICULUM_PROGRAM":
      return (
        currentCurriculumProgramId ||
        _.get(getJsonItem("currentCurriculumProgram"), "id")
      );
    case "COURSE":
      return courseId;
    case "IDENTITY":
      return identityId;
    case "GRADE":
      return gradeId || getUserPrimaryGradeId();
    case "LEARNING_COURSE":
      return learningCourseId;
    case "ACADEMIC_YEAR":
      return academicYearId;
  }
};

const getConfigurationEntityGroups = ({
  userData,
  entityTypes,
  hasChildContext,
  portalType,
}) => {
  return _.map(entityTypes, entityType => {
    const entityId = getEntityIdBasedOnEntityType({
      entityType,
      hasChildContext,
      portalType,
      ...userData,
    });
    return { entityId, entityType };
  });
};

const getEntityTypesForConfigurationMutation = ({
  appType,
  moduleName,
  src,
  courseId,
}) => {
  switch (src) {
    case "SETTINGS_PAGE": {
      if (appType === "ADMIN_WEB") {
        return _.includes(
          [
            "TURNITIN",
            "USER_NAME_SETTING",
            "USER_NAME_SETTING_V2",
            "PRINT_NAME_SETTINGS",
          ],
          moduleName
        )
          ? CONFIGURATION_ENTITY_LIST.ORGANIZATION_ONLY
          : CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY;
        // curriculum based settings
      } else {
        return courseId
          ? CONFIGURATION_ENTITY_LIST.COURSE_ONLY // course settings & course notification settings
          : CONFIGURATION_ENTITY_LIST.USER_ONLY;
      }
    }
    case "COURSE_LEVEL": // added this to set configurations on course level
    case "NOTIFICATION_CENTER":
      return CONFIGURATION_ENTITY_LIST.COURSE;
    case "UPDATE_USER_SETTINGS":
      return CONFIGURATION_ENTITY_LIST.USER_ONLY;
    case "UPDATE_COURSE_ONLY_SETTINGS":
      return CONFIGURATION_ENTITY_LIST.COURSE_ONLY;
    case "UPDATE_USER_CURRICULUM_SETTINGS":
      return CONFIGURATION_ENTITY_LIST.CURRICULUM;
    case "UPDATE_CURRICULUM_ONLY_SETTINGS":
      return CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY;
    case "IDENTITY_LEVEL":
      return CONFIGURATION_ENTITY_LIST.IDENTITY;
    case "MESSAGING_SETTINGS":
      return CONFIGURATION_ENTITY_LIST.ORGANIZATION_ONLY;
    case "IDENTITY_ONLY":
      return CONFIGURATION_ENTITY_LIST.IDENTITY_ONLY;
    case "LEARNING_COURSE_LEVEL":
      return CONFIGURATION_ENTITY_LIST.LEARNING_COURSE;
  }
};

const generateConfigurationMutationVariables = params => {
  // shouldStringifyValue is false when being called from `updateSettings` all the values passed to it are stringified
  const {
    configUpdateList,
    src,
    shouldStringifyValue = true,
    selectedAcademicYearId = null,
    isResetCurriculumLevelTags = false,
    isTeacherModuleSettings = false,
    isNotificationV2Module = false,
    skipPermissionsQuery = true,
  } = params;
  const entityTypes = getEntityTypesForConfigurationMutation(params);

  const variables = _.map(configUpdateList, configurationParam => {
    const { configuration, newValue, columnId, courseId } = configurationParam;
    const valueType = _.get(configuration, "valueType");

    if (
      !skipPermissionsQuery &&
      _.isEqual(columnId, "staff") &&
      !_.isEqual(_.get(configuration, "labelKey"), "excusals")
    ) {
      return null;
    }

    /* 'additionalEntities' are used to define the context on which the configuration's value is changed. ex., A single configuration can be used for both web/mobile or educator/student. Passing `additionalEntities` helps in setting the context */
    const additionalEntities = _.get(
      configuration,
      "metadata.additionalEntities"
    );

    const userData =
      src === "NOTIFICATION_CENTER"
        ? { courseId: configurationParam.courseId }
        : params;

    const configurationEntityGroups = getConfigurationEntityGroups({
      userData,
      entityTypes,
    });

    // create a mapping of columnId
    const columnIdMapping = _.reduce(
      configuration,
      (confResult, conf) => {
        const columnId = _.get(conf, "metadata.columnId");

        return { ...confResult, [columnId]: columnId };
      },
      {}
    );

    if (!columnId && !courseId && isNotificationV2Module) {
      const currentConfiguration = _.first(configuration) || configuration;
      const entityIdMapping = {
        inApp: "IN_APP",
        push: "PUSH",
        email: "EMAIL",
      };

      // create entity groups with enable columnId
      const entityGroups = _.map(columnIdMapping, columnId => {
        const entityId = _.get(entityIdMapping, columnId);
        return [
          ...configurationEntityGroups,
          {
            entityId: entityId,
            entityType: "CHANNEL",
          },
        ];
      });
      const configurationInput = {
        id: currentConfiguration?.id,
        valueType: "BOOLEAN",
        entityGroups: entityGroups,
        value: shouldStringifyValue
          ? stringifyConfigurationValue({
              valueType: "BOOLEAN",
              value: newValue,
            })
          : newValue,
      };
      return configurationInput;
    }

    const entityGroups = [...configurationEntityGroups];

    if (additionalEntities) entityGroups.push(...additionalEntities);

    const input = {
      id: configuration?.id,
      valueType,
      entityGroups: [entityGroups],
      value: shouldStringifyValue
        ? stringifyConfigurationValue({
            valueType,
            value: newValue,
          })
        : newValue,
    };

    if (isTeacherModuleSettings) {
      input.configureCustomCourseTags = "SET_COURSE_LEVEL_TAGS";
    } else if (isResetCurriculumLevelTags) {
      input.academicYearId = selectedAcademicYearId;
      input.configureCustomCourseTags = "RESET_CURRICULUM_LEVEL_TAGS";
    }
    return input;
  });

  return _.compact(variables);
};

const getDependentPermissionIds = ({
  moduleKey,
  permissionsQueryVariables,
}) => {
  const dependentPermissionsConfig =
    MODULE_DEPENDENT_CONTROL_PARAMETERS_CONFIG?.[moduleKey];

  if (
    !dependentPermissionsConfig ||
    dependentPermissionsConfig?.shouldSkip?.()
  ) {
    return [];
  }

  const rnpPermissions = getPermissionsFromCache(permissionsQueryVariables);
  const dependentPermissions =
    dependentPermissionsConfig?.controlParameters || [];

  return _.flatMap(rnpPermissions?.aclControlParameters || [], permission =>
    _.map(
      _.filter(permission.controlParameters, cp =>
        _.includes(dependentPermissions, cp.permission)
      ),
      "id"
    )
  );
};

const generateRnPPermissionsMutationVariables = ({
  configUpdateList,
  curriculumProgramId,
  courseId,
  moduleName,
  excludedCourseIdTag,
  selectedAcademicYearId,
  customCourses,
  permissionsQueryVariables,
  disableClassLevelModuleSetting = false,
}) => {
  const type = _.isEqual(moduleName, "MODULE_SETTINGS")
    ? "CURRICULUM"
    : "COURSES";
  const input = {
    metaDataInput: {},
  };
  const contexts = [];
  let excludedCourseIds = [];

  switch (type) {
    case "CURRICULUM":
      contexts.push({
        entityType: "CURRICULUM_PROGRAM",
        entityId: curriculumProgramId,
      });
      break;
    case "COURSES":
      contexts.push({
        entityType: "COURSE",
        entityId: courseId,
      });
      break;
  }
  input.contexts = contexts;
  const aclPermissions = [];

  _.reduce(
    configUpdateList,
    (acc, config) => {
      const {
        configuration,
        newValue,
        columnId,
        columnConfigurationList,
      } = config;
      if (!_.isEqual(columnId, "staff")) return acc;

      // get all the courses that are excluded
      _.map(columnConfigurationList, columnConfiguration => {
        const rnpId = _.get(columnConfiguration, "rnp.id");
        let courses = [];
        if (rnpId) {
          courses = _.get(customCourses, ["customCourseAclData", rnpId], []);
        } else {
          const configId = _.get(columnConfiguration, "id");
          courses = _.get(customCourses, ["customCourseData", configId], []);
        }
        excludedCourseIds = new Set([...excludedCourseIds, ...courses]);
      });

      const configId = _.get(configuration, "rnp.id");
      if (!configId || !_.isBoolean(newValue)) return acc;

      if (configuration?.labelKey === ATTENDANCE_CONSTANTS.ATTENDANCE) {
        const rnpPermissions = getPermissionsFromCache(
          permissionsQueryVariables
        );
        const attendancePermissionIds = getAttendancePermissionIds(
          rnpPermissions
        );
        attendancePermissionIds.forEach(id => {
          acc.push({
            id: id,
            value: newValue,
          });
        });
      }

      const dependentPermissionIds = getDependentPermissionIds({
        moduleKey: configuration?.labelKey,
        permissionsQueryVariables,
      });

      _.forEach(dependentPermissionIds, id => {
        acc.push({ id, value: newValue });
      });

      acc.push({
        id: configId,
        value: newValue,
      });
      return acc;
    },
    aclPermissions
  );

  if (_.isEmpty(aclPermissions)) {
    return {};
  }
  input.aclPermissions = aclPermissions;

  if (_.isEqual(type, "CURRICULUM") && !disableClassLevelModuleSetting) {
    input.configureCustomCourseTags = "RESET_CURRICULUM_LEVEL_TAGS";
    if (excludedCourseIdTag) {
      input.metaDataInput.excludedCourseIds = [...excludedCourseIds];
    }
  } else if (_.isEqual(type, "COURSES")) {
    input.configureCustomCourseTags = "SET_COURSE_LEVEL_TAGS";
  }

  if (selectedAcademicYearId) {
    input.metaDataInput.academicYearId = selectedAcademicYearId;
  }
  return input;
};

const getConfigurationViewQueryVariables = params => {
  const {
    src,
    appType,
    moduleName,
    curriculumProgramTypes,
    sourceFrom = "",
    currentCurriculumProgramType,
    currentCurriculumProgramId,
  } = params;

  const entityFilters = [{ entityType: "APP_TYPE", entityId: appType }];

  switch (src) {
    case "SETTINGS_PAGE":
      if (
        !_.includes(
          [
            "TURNITIN",
            "USER_NAME_SETTING",
            "USER_NAME_SETTING_V2",
            "PRINT_NAME_SETTINGS",
          ],
          moduleName
        ) &&
        appType !== "VISITOR_WEB"
      )
        if (sourceFrom === source) {
          if (
            _.includes(
              [
                "admin_notification_manager_tab_for_staff",
                "admin_notification_manager_tab_for_student",
                "admin_notification_manager_tab_for_parent",
              ],
              moduleName
            )
          ) {
            entityFilters.push({
              entityType: "CURRICULUM_TYPE",
              entityId: currentCurriculumProgramType,
            });
          } else {
            _.forEach(curriculumProgramTypes, currentCurriculumProgramType => {
              entityFilters.push({
                entityType: "CURRICULUM_TYPE",
                entityId: currentCurriculumProgramType,
              });
            });
          }
        } else {
          entityFilters.push({
            entityType: "CURRICULUM_TYPE",
            entityId: currentCurriculumProgramType,
          });
        }
      break;
  }

  if (moduleName === "MODULE_SETTINGS") {
    entityFilters.push({
      entityType: "CURRICULUM_PROGRAM",
      entityId: currentCurriculumProgramId,
    });
  }

  return {
    configurationViewFilters: { entityFilters },
  };
};

const getConfigurationViewQueryVariablesMemoized = _.memoize(
  getConfigurationViewQueryVariables,
  JSON.stringify
);

const checkSkipConfigurationViewQuery = params => {
  const { src, currentCurriculumProgramType, appType, moduleName } = params;
  switch (src) {
    case "SETTINGS_PAGE":
      if (
        !_.includes(
          [
            "TURNITIN",
            "USER_NAME_SETTING",
            "USER_NAME_SETTING_V2",
            "PRINT_NAME_SETTINGS",
            "NOTIFICATION_SETTINGS_V2",
          ],
          moduleName
        ) &&
        appType !== "VISITOR_WEB"
      )
        return !currentCurriculumProgramType || !appType;
      break;
  }
  return !appType;
};

/**
 * @deprecated This function is being deprecated in favor of `generateConfigurationQueryVariables` defined in `Utils/ConfigurationUtils` to avoid cyclic dependency
 */
const generateConfigurationQueryVariables = params => {
  const {
    src,
    organizationId,
    currentCurriculumProgramId,
    courseId,
    childId,
    curriculumProgramList,
    appType,
    moduleName,
    userId,
    portalType = "PLANNER",
    fetchInitialLoadConfigurations = false,
    configurations = [],
    isCourseModuleSetting = false,
    isAdminNotificationManager = false,
    learningCourseId,
    gradesList,
    modules = [],
  } = params;

  const userInfo = getUserInfo();
  let userType = userInfo.user_type;
  let identityId =
    params.identityId || userInfo.identityId || userInfo.identity_id;

  identityId = _.toString(identityId);

  const gradeId = params?.gradeId || getUserPrimaryGradeId();

  let entityTypes,
    configurationGroups,
    excludedConfigurationGroups,
    isAdminPage = false,
    isSettingsPage = false,
    _appType,
    excludeInterRepoConfigurations = true;

  const groupInput = {
    currentCurriculumProgramId,
    courseId,
    userId: isPublicPlatformRoute()
      ? fetchOrganizationAndUserIdForPublicPost().userId
      : userId,
    childId,
    identityId,
    gradeId,
    organizationId: isPublicPlatformRoute()
      ? fetchOrganizationAndUserIdForPublicPost().organizationId
      : organizationId,
    learningCourseId,
  };

  const isVisitor = _.isEqual(userInfo.role, ROLE_VISITOR);
  if (isVisitor) {
    userType = ROLE_VISITOR;
  }
  let groupInputList = [groupInput];

  const preloadedConfigurations = _.get(
    PRE_LOAD_CONFIGURATIONS,
    [userType, src],
    []
  );

  const hasChildContext = _.includes(
    ["CURRICULUM_LEVEL", "COURSE_LEVEL", "LEARNING_COURSE_LEVEL"],
    src
  ); // read child id for COURSE & CURRICULUM context only, otherwise normal userId should be used

  switch (src) {
    case "ORGANIZATION_LEVEL":
      // For staff, we can fetch identity level configurations along with (user + organization) level configurations
      entityTypes =
        !_.isEmpty(identityId) &&
        _.isEqual(userType, STAFF) &&
        !_.isEqual(portalType, "COMMUNITY")
          ? CONFIGURATION_ENTITY_LIST.ORGANIZATION_WITH_USER_AND_IDENTITY
          : CONFIGURATION_ENTITY_LIST.ORGANIZATION;
      break;
    case "IDENTITY_LEVEL":
      configurationGroups = ["IdentityAccess"];
      entityTypes = CONFIGURATION_ENTITY_LIST.IDENTITY;
      break;
    case "PARENT_AND_CURRICULUM_LEVEL":
    case "CURRICULUM_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;

      if (getIsModuleSettingsV2Enabled() && userType !== STAFF) {
        entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM_AND_GRADE;
      }

      break;
    case "PARENT_AND_COURSE_LEVEL":
      configurationGroups = ["SETTING"];
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE;
      break;
    case "COURSE_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE;
      break;
    case "SETTINGS_PAGE":
      configurationGroups = ["SETTING", "STATE_ROOT"];
      isSettingsPage = true;
      _appType = appType;
      excludeInterRepoConfigurations = false;

      if (appType === "ADMIN_WEB") {
        if (
          _.includes(
            [
              "TURNITIN",
              "USER_NAME_SETTING",
              "USER_NAME_SETTING_V2",
              "PRINT_NAME_SETTINGS",
            ],
            moduleName
          )
        )
          entityTypes = CONFIGURATION_ENTITY_LIST.ORGANIZATION;
        else
          entityTypes = isAdminNotificationManager
            ? CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY
            : CONFIGURATION_ENTITY_LIST.CURRICULUM;
        // curriculum settings
      } else
        entityTypes = courseId
          ? CONFIGURATION_ENTITY_LIST.COURSE_ONLY // Class setting
          : CONFIGURATION_ENTITY_LIST.USER_ONLY; // notification settings
      break;
    case "MODULE_SETTINGS_V2":
      configurationGroups = ["SETTING", "STATE_ROOT"];
      isSettingsPage = true;
      _appType = appType;
      excludeInterRepoConfigurations = false;
      entityTypes = _.isEqual(moduleName, "PROGRAM_MODULE_SETTINGS_V2")
        ? CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY
        : CONFIGURATION_ENTITY_LIST.ORGANIZATION_ONLY;
      if (!_.isEmpty(curriculumProgramList)) {
        groupInputList = _.map(curriculumProgramList, program => ({
          currentCurriculumProgramId: program.id,
        }));
      }
      break;
    case "GLOBAL_MODULE_SETTINGS":
      configurationGroups = ["SETTING", "STATE_ROOT"];
      isSettingsPage = true;
      _appType = appType;
      excludeInterRepoConfigurations = false;
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY;
      break;
    case "GLOBAL_MODULE_SETTINGS_GRADE_LEVEL":
      configurationGroups = ["SETTING", "STATE_ROOT"];
      isSettingsPage = true;
      _appType = appType;
      excludeInterRepoConfigurations = false;
      entityTypes = CONFIGURATION_ENTITY_LIST.GRADE_ONLY;
      if (!_.isEmpty(gradesList)) {
        groupInputList = _.map(gradesList, grade => ({
          gradeId: grade.id,
        }));
      }
      break;
    case "SCHOOL_LEVEL_COURSE_CLASS_SETTINGS":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      isSettingsPage = true;
      excludeInterRepoConfigurations = false;
      break;
    case "COURSE_LEVEL_COURSE_CLASS_SETTINGS":
      isSettingsPage = true;
      excludeInterRepoConfigurations = false;

      // Multiple entity group filters: one for CURRICULUM_PROGRAM, one for LEARNING_COURSE
      groupInputList = [
        {
          data: { currentCurriculumProgramId },
          entityTypes: CONFIGURATION_ENTITY_LIST.CURRICULUM,
        },
        {
          data: { learningCourseId },
          entityTypes: CONFIGURATION_ENTITY_LIST.LEARNING_COURSE,
        },
      ];

      break;
    case "NAVIGATION_SETTINGS":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM_WITH_LEARNING_COURSE;
      isSettingsPage = true;
      break;
    case "SETTINGS_AND_TEACHER_PERMISSION":
      configurationGroups = ["SETTING", "STATE_ROOT", "StaffPermission"];
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      break;
    case "TEACHER_PERMISSION":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      configurationGroups = ["StaffPermission", "LibraryPortal"];
      groupInputList = _.map(curriculumProgramList, program => ({
        currentCurriculumProgramId: program.id,
      }));
      break;
    case "ADMIN_PORTAL_HOME_PAGE":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      configurationGroups = ["AdminPortal", "Common", "SETTING"];
      groupInputList = _.map(curriculumProgramList, program => ({
        currentCurriculumProgramId: program.id,
      }));
      isAdminPage = true;
      break;
    case "COURSE_ONLY_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE_ONLY;
      break;
    case "GRADE_ONLY":
      entityTypes = CONFIGURATION_ENTITY_LIST.GRADE_ONLY;
      break;
    case "IDENTITY_ONLY":
      entityTypes = CONFIGURATION_ENTITY_LIST.IDENTITY;
      configurationGroups = ["IdentityAccess"];
      break;
    case "LEARNING_COURSE_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.LEARNING_COURSE;
      break;
    case "PARENT_AND_LEARNING_COURSE_LEVEL":
      configurationGroups = ["SETTING"];
      entityTypes = CONFIGURATION_ENTITY_LIST.LEARNING_COURSE;
      break;
    case "HOME_PAGE":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      groupInputList = _.map(curriculumProgramList, program => ({
        currentCurriculumProgramId: program.id,
      }));
      break;
  }

  if (portalType === "COMMUNITY")
    configurationGroups = ["CommunityPortal", "LibraryPortal"];

  const configurationEntityGroupsList = _.map(groupInputList, groupInput => {
    // Check if groupInput has its own entityTypes (for cases like COURSE_LEVEL_COURSE_CLASS_SETTINGS)
    const groupEntityTypes = _.get(groupInput, "entityTypes", entityTypes);
    const groupData = _.get(groupInput, "data", groupInput);

    return getConfigurationEntityGroups({
      userData: groupData,
      entityTypes: groupEntityTypes,
      hasChildContext,
      portalType,
    });
  });

  const configurationEntityGroupFilters = _.map(
    configurationEntityGroupsList,
    configurationEntityGroups => ({
      configurationEntityGroups,
      configurationGroups,
      excludedConfigurationGroups,
      appType: _appType, // send appType only for queries from setting pages
      excludeInterRepoConfigurations,
      configurations: !_.isEmpty(configurations)
        ? configurations
        : fetchInitialLoadConfigurations
        ? preloadedConfigurations
        : [],
      ...(modules.length > 0 && { modules }),
    })
  );

  return {
    configurationEntityGroupFilters,
    isAdminPage,
    isSettingsPage,
    portalType,
    isCourseModuleSetting,
    isAdminNotificationManager,
  };
};

const generateConfigurationQueryVariablesMemoized = _.memoize(
  generateConfigurationQueryVariables,
  JSON.stringify
);

//TODO: Remove this function as its moved to Utils/ConfigurationUtils.js
const getConfigurationValue = inputProps => {
  try {
    const { configName, readFromUserAndOrgLevel } = inputProps;

    const CONFIGURATION_READ_PRECEDENCE = [
      "GRADE_ONLY",
      "PARENT_AND_COURSE_LEVEL",
      "COURSE_LEVEL",
      "PARENT_AND_LEARNING_COURSE_LEVEL",
      "LEARNING_COURSE_LEVEL",
      "PARENT_AND_CURRICULUM_LEVEL",
      "CURRICULUM_LEVEL",
      "ORGANIZATION_LEVEL",
    ];
    const PRECEDENCE_LEVELS = readFromUserAndOrgLevel
      ? ["ORGANIZATION_LEVEL"]
      : CONFIGURATION_READ_PRECEDENCE;

    const getConfigurationValueBasedOnPrecedence = () => {
      let value;
      for (const src of PRECEDENCE_LEVELS) {
        const configurationVariables = generateConfigurationQueryVariables({
          ...inputProps,
          src,
        });

        const configuration = readConfigurationFragmentV2(
          configurationVariables,
          configName
        );

        const configurationValue = parseConfigurationValue(configuration);
        if (!_.isNil(configurationValue)) {
          value = configurationValue;
          break;
        }
      }
      return value;
    };

    const val = getConfigurationValueBasedOnPrecedence();
    return val;
  } catch (err) {
    console.error(err);
  }
};

const getAllCurriculumsAdminConfigurationsObject = ({
  curriculumProgramList,
}) => {
  const configurationVariables = generateConfigurationQueryVariables({
    src: "ADMIN_PORTAL_HOME_PAGE",
    curriculumProgramList,
  });

  const curriculumBasedConfig = getConfigurationsFromCacheV2(
    configurationVariables
  );

  return _.reduce(
    curriculumBasedConfig,
    (curriculumConfigObj, config) => {
      const { configurations, entityGroups } = config;
      const configMap = _.keyBy(configurations, "configuration");
      const matchedGroup = _.find(entityGroups, {
        entityType: "CURRICULUM_PROGRAM",
      });
      const curriculumProgramId = _.get(matchedGroup, "entityId");
      return {
        ...curriculumConfigObj,
        [curriculumProgramId]: configMap,
      };
    },
    {}
  );
};

const skipPreloadConfigurationsQuery = () => {
  return isAdministratorRoute();
};
export {
  generateConfigurationQueryVariables,
  generateConfigurationMutationVariables,
  generateRnPPermissionsMutationVariables,
  getConfigurationValue,
  parseConfigurationValue,
  getConfigurationViewQueryVariables,
  getConfigurationEntityGroups,
  checkSkipConfigurationViewQuery,
  getAllCurriculumsAdminConfigurationsObject,
  skipPreloadConfigurationsQuery,
  generateConfigurationQueryVariablesMemoized,
  getConfigurationViewQueryVariablesMemoized,
};
